import React, { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import akhiratLogo from '../../assets/images/akhirat-logo.png'
import { inheritanceClaimsService, ClaimStatusResponse } from '../../services/inheritanceClaims.service'

const MAX_FILE_BYTES = 10 * 1024 * 1024
const ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp'

const inputClass =
  'w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:border-emerald-500 focus:ring-emerald-500'
const labelClass = 'block text-sm font-medium text-neutral-700 mb-1'

export default function InheritanceClaimPage() {
  const fileRef = useRef<HTMLInputElement>(null)
  const [fields, setFields] = useState({
    deceasedAccountId: '',
    submittedByMemberId: '',
    submittedByNric: '',
    submittedByName: '',
    submittedByEmail: '',
    submittedByPhone: '',
    relationship: '',
    bankName: '',
    bankAccountNumber: '',
    bankAccountHolderName: '',
  })
  const [file, setFile] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [claimNumber, setClaimNumber] = useState('')

  // Status check
  const [statusNumber, setStatusNumber] = useState('')
  const [statusNric, setStatusNric] = useState('')
  const [statusResult, setStatusResult] = useState<ClaimStatusResponse | null>(null)
  const [statusError, setStatusError] = useState('')
  const [checking, setChecking] = useState(false)

  const set = (key: keyof typeof fields) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setFields((prev) => ({ ...prev, [key]: e.target.value }))

  const pickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    if (f.size > MAX_FILE_BYTES) {
      setError('The death certificate must be 10 MB or smaller.')
      e.target.value = ''
      return
    }
    setFile(f)
    setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!file) {
      setError('Please attach the death certificate (PDF or image).')
      return
    }
    setSubmitting(true)
    try {
      const result = await inheritanceClaimsService.submit(fields, file)
      setClaimNumber(result.claimNumber)
      window.scrollTo({ top: 0 })
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to submit the claim. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleStatusCheck = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatusError('')
    setStatusResult(null)
    setChecking(true)
    try {
      setStatusResult(await inheritanceClaimsService.getStatus(statusNumber, statusNric))
    } catch (err: any) {
      setStatusError(err?.response?.data?.message || 'No claim found for those details')
    } finally {
      setChecking(false)
    }
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-10">
      <div className="mx-auto max-w-3xl px-4">
        <div className="mb-8 flex items-center gap-3">
          <img src={akhiratLogo} alt="Akhirat" className="h-10 w-10 object-contain" />
          <div>
            <h1 className="text-xl font-bold text-neutral-900">Inheritance Claim</h1>
            <p className="text-sm text-neutral-500">Claim the account balance of a deceased Akhirat member.</p>
          </div>
        </div>

        {claimNumber ? (
          <div className="rounded-lg border border-green-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-green-700">Claim submitted</h2>
            <p className="mt-2 text-sm text-neutral-600">
              Your claim reference is{' '}
              <span className="font-mono font-semibold text-neutral-900">{claimNumber}</span>. Keep
              it safe — you will need it together with your NRIC to check the status. Our team will
              review the death certificate and contact you.
            </p>
            <div className="mt-6 border-t border-neutral-200 pt-5">
              <h3 className="mb-3 text-sm font-semibold text-neutral-900">Check your claim status</h3>
              <form onSubmit={handleStatusCheck} className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <input
                  className={inputClass}
                  placeholder="Claim number"
                  value={statusNumber}
                  onChange={(e) => setStatusNumber(e.target.value)}
                />
                <input
                  className={inputClass}
                  placeholder="Your NRIC / passport"
                  value={statusNric}
                  onChange={(e) => setStatusNric(e.target.value)}
                />
                <button
                  type="submit"
                  disabled={checking}
                  className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-40"
                >
                  {checking ? 'Checking…' : 'Check status'}
                </button>
              </form>
              {statusResult && (
                <p className="mt-3 text-sm text-neutral-700">
                  Claim <span className="font-mono">{statusResult.claimNumber}</span> status:{' '}
                  <span className="font-semibold">{statusResult.status}</span>
                </p>
              )}
              {statusError && <p className="mt-3 text-sm text-red-600">{statusError}</p>}
            </div>
            <Link to="/" className="mt-6 inline-block text-sm text-primary-600 hover:underline">
              ← Back to home
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-6 rounded-lg border border-neutral-200 bg-white p-5 text-sm text-neutral-600">
              <p>
                This form is for <strong>named beneficiaries</strong> of a deceased Akhirat member's
                will. You will need the <strong>Account ID</strong> printed on the member's e-card or
                will letter, <strong>your beneficiary ID</strong> (60-prefix, on the will letter), and
                the <strong>death certificate</strong>.
              </p>
            </div>

            {error && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>Deceased member's Account ID</label>
                  <input className={inputClass} value={fields.deceasedAccountId} onChange={set('deceasedAccountId')} placeholder="e.g. 10000045" required />
                </div>
                <div>
                  <label className={labelClass}>Your Beneficiary ID (60-prefix)</label>
                  <input className={inputClass} value={fields.submittedByMemberId} onChange={set('submittedByMemberId')} placeholder="e.g. 60000017" required />
                </div>
                <div>
                  <label className={labelClass}>Your NRIC / Passport</label>
                  <input className={inputClass} value={fields.submittedByNric} onChange={set('submittedByNric')} placeholder="Must match our records" required />
                </div>
                <div>
                  <label className={labelClass}>Your full name</label>
                  <input className={inputClass} value={fields.submittedByName} onChange={set('submittedByName')} required />
                </div>
                <div>
                  <label className={labelClass}>Email</label>
                  <input className={inputClass} type="email" value={fields.submittedByEmail} onChange={set('submittedByEmail')} />
                </div>
                <div>
                  <label className={labelClass}>Phone</label>
                  <input className={inputClass} value={fields.submittedByPhone} onChange={set('submittedByPhone')} />
                </div>
                <div>
                  <label className={labelClass}>Relationship to the deceased</label>
                  <input className={inputClass} value={fields.relationship} onChange={set('relationship')} placeholder="e.g. Sibling" />
                </div>
              </div>

              <div>
                <label className={labelClass}>Death certificate (PDF or image, max 10 MB)</label>
                <input ref={fileRef} type="file" accept={ACCEPT} onChange={pickFile} className="block w-full text-sm text-neutral-700 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-700 hover:file:bg-primary-100" />
              </div>

              <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                <h3 className="mb-3 text-sm font-semibold text-neutral-900">Your bank account (for the payout)</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <label className={labelClass}>Bank name</label>
                    <input className={inputClass} value={fields.bankName} onChange={set('bankName')} required />
                  </div>
                  <div>
                    <label className={labelClass}>Account number</label>
                    <input className={inputClass} value={fields.bankAccountNumber} onChange={set('bankAccountNumber')} required />
                  </div>
                  <div>
                    <label className={labelClass}>Account holder name</label>
                    <input className={inputClass} value={fields.bankAccountHolderName} onChange={set('bankAccountHolderName')} required />
                  </div>
                </div>
                <p className="mt-2 text-xs text-neutral-500">
                  Other named beneficiaries will be contacted separately for their bank details.
                </p>
              </div>

              <p className="text-xs text-neutral-500">
                Submissions are reviewed by our team against the member's will and the death
                certificate. Submitting a false claim is a serious offence.
              </p>

              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-md bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-40"
              >
                {submitting ? 'Submitting…' : 'Submit Inheritance Claim'}
              </button>
            </form>

            <div className="mt-6 rounded-lg border border-neutral-200 bg-white p-5">
              <h3 className="mb-3 text-sm font-semibold text-neutral-900">Already filed? Check your claim status</h3>
              <form onSubmit={handleStatusCheck} className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <input className={inputClass} placeholder="Claim number" value={statusNumber} onChange={(e) => setStatusNumber(e.target.value)} />
                <input className={inputClass} placeholder="Your NRIC / passport" value={statusNric} onChange={(e) => setStatusNric(e.target.value)} />
                <button
                  type="submit"
                  disabled={checking}
                  className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-40"
                >
                  {checking ? 'Checking…' : 'Check status'}
                </button>
              </form>
              {statusResult && (
                <p className="mt-3 text-sm text-neutral-700">
                  Claim <span className="font-mono">{statusResult.claimNumber}</span> status:{' '}
                  <span className="font-semibold">{statusResult.status}</span>
                </p>
              )}
              {statusError && <p className="mt-3 text-sm text-red-600">{statusError}</p>}
            </div>

            <Link to="/" className="mt-6 inline-block text-sm text-primary-600 hover:underline">
              ← Back to home
            </Link>
          </>
        )}
      </div>
    </div>
  )
}
